import { useEffect, useState, useRef, useCallback } from 'react';
import { useComlinkWorker, createComlinkWorkerFactory } from "hooks/useComlinkWorker";
import { setTensorflowWorker, loadModelsIntoWorker } from "services/models";
import { WORKER_CONFIG } from '../constants/worker';
import { sleep } from '../utils/workerHelpers';
import type { WorkerApiInterface } from 'types/models';

const workerFactory = createComlinkWorkerFactory(() => import("../workers/tensorflow.worker.ts?worker"));

/**
 * Hook for loading and managing TensorFlow models in a web worker
 */
const useModels = () => {
  const [isDoneLoadingModels, setIsDoneLoadingModels] = useState(false);
  const hasStartedLoadingRef = useRef(false);
  const isLoadingModelsRef = useRef(false);
  const lastWorkerRef = useRef<unknown>(null);
  const worker = useComlinkWorker<WorkerApiInterface>(workerFactory);

  // Set worker in services/models when available; null it out on unmount
  useEffect(() => {
    if (worker) {
      setTensorflowWorker(worker);
    }
    return () => {
      // [M1-FIX] Null the worker reference on unmount so models.ts doesn't
      // hold a stale reference to a terminated Comlink proxy
      setTensorflowWorker(null);
    };
  }, [worker]);

  /**
   * Load models into the worker via the shared service routine
   */
  const loadModels = useCallback(async () => {
    if (!worker) {
      console.warn('Models: Worker not ready');
      return;
    }
    if (isLoadingModelsRef.current || isDoneLoadingModels) {
      return;
    }

    isLoadingModelsRef.current = true;

    try {
      const result = await loadModelsIntoWorker("initial");
      if (result.success) {
        setIsDoneLoadingModels(true);
      }
    } finally {
      isLoadingModelsRef.current = false;
    }
  }, [worker, isDoneLoadingModels]);

  useEffect(() => {
    if (worker && worker !== lastWorkerRef.current) {
      lastWorkerRef.current = worker;
      hasStartedLoadingRef.current = false;
      isLoadingModelsRef.current = false;
    }
    if (worker && !hasStartedLoadingRef.current) {
      hasStartedLoadingRef.current = true;
      // Small delay to ensure worker is fully initialized
      sleep(WORKER_CONFIG.INITIALIZATION_DELAY).then(() => {
        loadModels();
      });
    }
  }, [worker, loadModels]);

  return {
    isDoneLoadingModels,
  };
};

export default useModels;
